import { fetchJSON, postJSON, patchJSON, deleteJSON, fetchJSONResult, patchJSONResult, postJSONResult } from './client';
import { withProjectQuery } from './projectContext';
import type {
  ProjectMeta,
  SkillInfo,
  TriageResult,
  SeparationResult,
  ExtractionResult,
  BoqExtractionResult,
  FloorPlanAnnotationResult,
  ResearchReport,
  PromptEntry,
  ApprovalRequest,
  ExtractionPageResult,
  ReviewData,
  MatchSummary,
  LvMatch,
  CablesResult,
} from '../types';

// ── Auth ──────────────────────────────────────────────────

export interface AuthUser {
  id: number;
  username: string;
  role: 'owner' | 'annotator';
}

export const authLogin = (username: string, password: string) =>
  postJSON<{ user: AuthUser }>('/api/auth/login', { username, password });
export const authLogout = () => postJSON('/api/auth/logout');
export const authMe = () => fetchJSON<AuthUser>('/api/auth/me');
export const authRegister = (username: string, password: string, role: string = 'annotator') =>
  postJSON<AuthUser>('/api/auth/register', { username, password, role });
export const authListUsers = () => fetchJSON<AuthUser[]>('/api/auth/users');
export const authDeleteUser = (userId: number) => deleteJSON(`/api/auth/users/${userId}`, {});

// ── Admin: annotator (monco-bb-copy) power control (owner-only) ──

export interface AnnotatorActivity {
  active_5min: number;
  active_15min: number;
  last_activity_seconds_ago: number | null;
  users?: { username: string; seconds_ago: number }[] | null;
}

export interface AnnotatorStatus {
  state: 'running' | 'stopped' | 'pending' | 'stopping' | 'shutting-down' | 'terminated';
  instance_type?: string;
  public_ip?: string | null;
  url?: string;
  activity?: AnnotatorActivity | null;
}

export const annotatorStatus = () =>
  fetchJSON<AnnotatorStatus>('/api/admin/annotator/status');
export const annotatorStart = () =>
  postJSON<{ state: string; action: string }>('/api/admin/annotator/start');
export const annotatorStop = () =>
  postJSON<{ state: string; action: string }>('/api/admin/annotator/stop');

// ── Project Management / Team ─────────────────────────────

export interface ProjectUser {
  id: number;
  username: string;
  user_role: string;
  project_role: string;
  granted_at: string;
}

export interface PageAssignment {
  id: number;
  project_id: string;
  user_id: number;
  username?: string;
  name: string;
  file: string;
  page: number;
  doc_type: string | null;
  zone_id: string | null;
  status: 'assigned' | 'in_progress' | 'completed' | 'skipped';
  assigned_at: string;
  completed_at: string | null;
}

export const pmListProjects = () =>
  fetchJSON<{ id: string; name: string; s3_uri?: string; access_role: string }[]>('/api/pm/projects');
export const pmGetProject = (projectId: string) =>
  fetchJSON<{ id: string; name: string; access_role: string }>(`/api/pm/projects/${projectId}`);
export const pmGetProjectUsers = (projectId: string) =>
  fetchJSON<ProjectUser[]>(`/api/pm/projects/${projectId}/users`);
export const pmGrantAccess = (projectId: string, userId: number, role: string = 'annotator') =>
  postJSON(`/api/pm/projects/${projectId}/access`, { user_id: userId, role });
export const pmRevokeAccess = (projectId: string, userId: number) =>
  deleteJSON(`/api/pm/projects/${projectId}/access/${userId}`, {});
export const pmAssignPages = (projectId: string, userId: number, pages: { file: string; page: number; doc_type?: string; zone_id?: string; name?: string }[]) =>
  postJSON<PageAssignment[]>(`/api/pm/projects/${projectId}/assignments/assign`, { user_id: userId, pages });
export const pmGetAssignments = (projectId: string) =>
  fetchJSON<PageAssignment[]>(`/api/pm/projects/${projectId}/assignments`);
export const pmGetMyAssignments = (projectId: string) =>
  fetchJSON<PageAssignment[]>(`/api/pm/projects/${projectId}/assignments/mine`);

// Floor-plan layout detection (owner-triggered): per-page record status read
// from S3 (agent_output/layout/<file_stem>/page_<n>.json). 'stale' =
// queued/processing with no update for over an hour (the job died; retry).
export type LayoutPageStatus = 'queued' | 'processing' | 'ok' | 'no_layout' | 'failed' | 'stale' | 'unknown';
export interface LayoutPageInfo {
  status: LayoutPageStatus;
  error: string;
  /** The result's own explanation (why no layout, or what is unverified). */
  reason?: string;
  /** e.g. 'no_scale': a layout came out, but its sizes are not to scale. */
  warnings?: string[];
  updated_at: string | null;
}
export interface LayoutStatusResponse {
  files: Record<string, { pages: Record<string, LayoutPageInfo> }>;
  /** Triage floor-plan pages per file stem: what "Extract layout" covers. */
  floor_plans: Record<string, number[]>;
  error?: string;
}
export const pmGetLayoutStatus = (projectId: string) =>
  fetchJSON<LayoutStatusResponse>(`/api/pm/projects/${projectId}/layout-status`);
export interface LayoutExtractResult {
  file: string;
  submitted: number[];
  skipped: { page: number; reason: string }[];
  failed: { page: number; error: string }[];
  dry_run: boolean;
}
// Submit layout jobs for a file's floor-plan pages (all, or `pages`). The
// preview is the same call as a dry run: nothing is written or submitted.
export const pmLayoutExtract = (projectId: string, file: string, pages?: number[]) =>
  postJSONResult<LayoutExtractResult>(`/api/pm/projects/${projectId}/layout/extract`, { file, pages });
export const pmLayoutExtractPreview = (projectId: string, file: string, pages?: number[]) =>
  fetchJSONResult<LayoutExtractResult>(
    `/api/pm/projects/${projectId}/layout/extract-preview?file=${encodeURIComponent(file)}`
    + (pages?.length ? `&pages=${pages.join(',')}` : ''));
// One page's layout export (schema monco.layout_export v1). All coordinates
// are PDF points of the displayed page, origin top-left (pdf.js
// getViewport({scale:1}) space). Rings do not repeat their first point.
type Pt = [number, number];
export interface LayoutWall { id: string; centerline: Pt[]; thickness_m: number | null; outline: Pt[] }
export interface LayoutOpening { id: string; segment: [Pt, Pt]; width_m: number | null; rooms: (string | null)[] }
// A room's name is its number and its name in German / English; the names never hold the number.
export interface LayoutRoom {
  id: string; number?: string | null; name_de?: string | null; name_en?: string | null;
  area_m2: number | null; printed_area_m2: number | null;
  zone_id: string | null; outdoor: boolean; polygon: Pt[];
}
export interface LayoutZone {
  id: string; name: string | null; kind: string | null; summary: string | null;
  room_ids: string[]; polygon: Pt[]; parts?: Pt[][]; holes?: Pt[][];
}
export interface LayoutExport {
  schema: string; schema_version: number; status: string; reason?: string | null;
  warnings?: string[];
  units?: { page_size_pt?: Pt; m_per_unit?: number; scale?: number };
  region?: number[] | null;
  walls?: LayoutWall[]; doors?: LayoutOpening[]; windows?: LayoutOpening[];
  rooms?: LayoutRoom[]; zones?: LayoutZone[];
}
export const pmGetLayoutPage = (projectId: string, fileStem: string, page: number) =>
  fetchJSON<LayoutExport>(
    `/api/pm/projects/${encodeURIComponent(projectId)}/layout/${encodeURIComponent(fileStem)}/${page}`);
export const pmUpdateAssignment =(assignmentId: number, status: string) =>
  patchJSON(`/api/pm/assignments/${assignmentId}`, { status });
export const pmRenameAssignment = (assignmentId: number, name: string) =>
  patchJSON(`/api/pm/assignments/${assignmentId}/rename`, { name });
// Reassign to another user — changes the assignee only (preserves status,
// timestamps, work) and re-sends the assignment email. Distinct from
// pmAssignPages, which upserts and resets status to 'assigned'.
export const pmReassignAssignment = (assignmentId: number, userId: number) =>
  patchJSON<PageAssignment>(`/api/pm/assignments/${assignmentId}/reassign`, { user_id: userId });

// ── Data ──────────────────────────────────────────────────

export const getProject = () => fetchJSON<ProjectMeta>('/api/project');
export const getSkills = () => fetchJSON<SkillInfo[]>('/api/skills');
export const getResults = <T = unknown>(skill: string) => fetchJSON<T>(`/api/results/${skill}`);
export const getPrompts = () => fetchJSON<PromptEntry[]>('/api/prompts');
export const getSessionStatus = () => fetchJSON<{ session_id: string; hitl?: boolean; multi_project?: boolean }>('/api/session-status');
export const getRenders = () => fetchJSON<Record<string, string[]>>('/api/renders');
// Map each source file (basename) → ISO date it was added to S3. Used by
// the annotation tab to show how "new" a file is. Empty for local projects.
export const getFileAddedDates = () => fetchJSON<Record<string, string>>('/api/files/added-dates');
export const getExtractionPageResults = () =>
  fetchJSON<Record<string, ExtractionPageResult>>('/api/extraction-page-results');
export const getPendingApprovals = () => fetchJSON<ApprovalRequest[]>('/api/pending-approvals');

// ── Typed skill results ────────────────────────────────────

export const getTriage = () => getResults<TriageResult>('triage');
export const getSeparation = () => getResults<SeparationResult>('separation');
export const getExtraction = () => getResults<ExtractionResult>('diagram_extraction');
export const getBoqExtraction = () => getResults<BoqExtractionResult>('boq_extraction');
export const getResearchReport = () => getResults<ResearchReport>('research_report');
export const getFloorPlanAnnotation = () => getResults<FloorPlanAnnotationResult>('floor_plan_annotation');
export const getPreScan = () => getResults<unknown>('pre_scan');

// ── PDF URL ────────────────────────────────────────────────

export const pdfUrl = (filename: string) =>
  withProjectQuery(`/api/pdf/${encodeURIComponent(filename)}`);
export const renderUrl = (skill: string, filePage: string) =>
  withProjectQuery(`/api/render/${skill}/${filePage}`);

// ── HITL approval ──────────────────────────────────────────

export const approve = (requestId: string, instructions?: string) =>
  postJSON('/api/approve', { request_id: requestId, instructions });

export const reject = (requestId: string, notes?: string) =>
  postJSON('/api/reject', { request_id: requestId, notes });

export const approveResult = (requestId: string) =>
  postJSON('/api/approve-result', { request_id: requestId });

export const rerun = (requestId: string, instructions?: string) =>
  postJSON('/api/rerun', { request_id: requestId, instructions });

export const approvePageReview = (
  requestId: string,
  opts?: {
    content_bbox?: unknown;
    content_bboxes?: number[][];
    context_blocks?: unknown;
    instructions?: string;
  },
) =>
  postJSON('/api/approve-page-review', { request_id: requestId, ...opts });

export const skipPageReview = (requestId: string) =>
  postJSON('/api/skip-page-review', { request_id: requestId });

// ── Component CRUD ─────────────────────────────────────────

export const editComponent = (
  zoneId: string,
  file: string,
  page: number,
  componentId: string,
  updates: Record<string, unknown>,
) =>
  patchJSON('/api/extraction/component', {
    zone_id: zoneId,
    file,
    page,
    component_id: componentId,
    updates,
  });

export const deleteComponent = (
  zoneId: string,
  file: string,
  page: number,
  componentId: string,
) =>
  deleteJSON('/api/extraction/component', {
    zone_id: zoneId,
    file,
    page,
    component_id: componentId,
  });

export const addComponent = (
  zoneId: string,
  file: string,
  page: number,
  component: Record<string, unknown>,
) =>
  postJSON('/api/extraction/component', {
    zone_id: zoneId,
    file,
    page,
    component,
  });

export const duplicateComponent = (
  zoneId: string,
  file: string,
  page: number,
  sourceComponentId: string,
  newId: string,
) =>
  postJSON('/api/extraction/duplicate-component', {
    zone_id: zoneId,
    file,
    page,
    source_component_id: sourceComponentId,
    new_id: newId,
  });

export const reExtractPage = (
  zoneId: string,
  file: string,
  page: number,
  opts?: { instructions?: string; content_bbox?: unknown; content_bboxes?: number[][]; context_blocks?: unknown },
) =>
  postJSON('/api/extraction/re-extract-page', {
    zone_id: zoneId,
    file,
    page,
    ...opts,
  });

export const extractDocument = (
  file: string,
  zoneId: string = 'default',
  instructions?: string,
) =>
  postJSON<{ status: string; file: string; pages_extracted: number; total_components: number }>(
    '/api/extraction/extract-document',
    { file, zone_id: zoneId, instructions },
  );

export const extractAll = (opts?: { instructions?: string; max_parallel?: number }) =>
  postJSON<{
    status: string;
    files_processed: number;
    files_failed: number;
    total_components: number;
    files: { file: string; pages_extracted: number; total_components: number }[];
    errors: { file: string; error: string }[];
  }>('/api/extraction/extract-all', {
    instructions: opts?.instructions,
    max_parallel: opts?.max_parallel ?? 4,
  });

// ── Page Render (tiled) ───────────────────────────────────

export const preparePage = (file: string, page: number) =>
  postJSON<{
    display_url: string;
    tiles_url_prefix: string | null;
    full_res_url: string;
    width: number;
    height: number;
    display_width: number;
    display_height: number;
    tile_cols: number;
    tile_rows: number;
    tile_size: number;
  }>('/api/page/prepare', { file, page });

export const pageImageUrl = (fileStem: string, page: number) =>
  withProjectQuery(`/api/page/image/${encodeURIComponent(fileStem)}/${page}`);

export const pageTileUrl = (fileStem: string, page: number, row: number, col: number) =>
  withProjectQuery(`/api/page/tiles/${encodeURIComponent(fileStem)}/${page}/tile_${row}_${col}.png`);

// ── Chat ──────────────────────────────────────────────────

export const chat = (
  message: string,
  history: { role: string; text: string }[],
  images?: { data: string; mime_type: string; label: string }[],
  attachedPages?: { filename: string; pages: number[] }[],
) =>
  postJSON<{ response: string }>('/api/chat', {
    message,
    history,
    images: images && images.length > 0 ? images : undefined,
    attached_pages: attachedPages && attachedPages.length > 0 ? attachedPages : undefined,
  });

// ── Review ────────────────────────────────────────────────

export const prepareReview = () => postJSON<ReviewData>('/api/review/prepare');
export const getReviewData = () => fetchJSON<ReviewData>('/api/review/data');
export const updateReviewMatch = (itemIndex: number, lvMatch: LvMatch | null) =>
  patchJSON<{ match_summary: MatchSummary }>('/api/review/match', { item_index: itemIndex, lv_match: lvMatch });
export const publishReview = () =>
  postJSON<{ status: string; location: string; artifacts: number; boq_items: number }>('/api/review/publish');
export const publishPlansReview = (file?: string, page?: number) =>
  postJSON<{ status: string; location: string; artifacts: number; plans: number }>('/api/review/plans/publish',
    file != null && page != null ? { file, page } : {});
// targetProjectId switches the backend into org-copy mode: it uploads to that
// project and touches none of this project's published_plans.json bookkeeping.
export const uploadPlanToDb = (file: string, page: number, targetProjectId?: number) =>
  postJSON<{ floor_plan_id: number; plan_name: string; deleted_floor_plan_id: number | null }>(
    '/api/review/plans/upload-to-db',
    targetProjectId != null ? { file, page, target_project_id: targetProjectId } : { file, page });
// Result variant used by "Upload all": keeps the HTTP status so a 409
// (plan currently being auto-processed — skipped) can be reported
// separately from a real failure.
export const uploadPlanToDbResult = (file: string, page: number) =>
  postJSONResult<{ floor_plan_id: number; plan_name: string; deleted_floor_plan_id: number | null }>(
    '/api/review/plans/upload-to-db', { file, page });
// Push the page's detected layout (zones, rooms, walls, doors, windows) into
// the Monco floor plan recorded by the last "Upload to DB". Needs that
// floor_plan_id and an 'ok' layout record; the server replaces the whole set.
export interface PlanLayoutUploadResult {
  floor_plan_id: number;
  zonesCreated: number;
  roomsCreated: number;
  wallsCreated: number;
  doorsCreated: number;
  windowsCreated: number;
  scaleApplied: boolean;
  routesRecomputed: number;
  /** Geometry the export carried but Monco would reject, per collection. */
  skipped: Record<string, number>;
}
export const uploadPlanLayoutToDbResult = (file: string, page: number) =>
  postJSONResult<PlanLayoutUploadResult>('/api/review/plans/upload-layout-to-db', { file, page });
export const syncAnnotations = () =>
  postJSON<{ synced: number; skipped: number; errors: string[] }>('/api/review/sync-annotations');
export const updatePlanMatchItem = (file: string, page: number, itemIndex: number, lvMatch: LvMatch | null) =>
  patchJSON<{ boq_items: unknown[]; match_summary: MatchSummary }>('/api/review/plans/match-item', {
    file, page, item_index: itemIndex, lv_match: lvMatch,
  });

// ── Floor-plan name syntax check + duplicate matching ──────
// Proofread every group / cable-type name across the project's floor plans,
// find near-identical names across plans, and apply the accepted renames to
// both the agent's per-page files and bb-copy's source of truth.

export type NameKind = 'group' | 'cable_type';

export interface NameOccurrence {
  file: string;
  page: number;
}

export interface SyntaxSuggestion {
  id: string;
  kind: NameKind;
  original: string;
  suggested: string;
  reason: string;
  occurrences: NameOccurrence[];
}

export interface SyntaxWarning {
  id: string;
  kind: NameKind;
  name: string;
  reason: string;
  occurrences: NameOccurrence[];
}

export interface SyntaxCheckResult {
  suggestions: SyntaxSuggestion[];
  warnings: SyntaxWarning[];
}

export interface DuplicateMember {
  name: string;
  occurrences: NameOccurrence[];
}

export interface DuplicateCluster {
  kind: NameKind;
  members: DuplicateMember[];
  suggested: string;
  reason: string;
}

export interface DuplicateResult {
  clusters: DuplicateCluster[];
}

export interface SyntaxFix {
  kind: NameKind;
  original: string;
  suggested: string;
  occurrences: NameOccurrence[];
}

export interface SyntaxApplyResult {
  renamed: { group: number; cable_type: number };
  merged: number;
  pages_touched: NameOccurrence[];
  bb_synced: { file: string; page: number; assignment_id: number }[];
  bb_skipped: { file: string; page: number; reason: string }[];
  errors: string[];
}

// Optional `scope` restricts the check to the given floor plans; omit/empty
// checks every floor plan in the project (whole-project default).
export const syntaxCheckPlans = (scope?: NameOccurrence[]) =>
  postJSONResult<SyntaxCheckResult>('/api/review/plans/syntax-check', scope?.length ? { scope } : {});

export const findDuplicatePlans = (scope?: NameOccurrence[]) =>
  postJSONResult<DuplicateResult>('/api/review/plans/find-duplicates', scope?.length ? { scope } : {});

export const applySyntaxFixes = (fixes: SyntaxFix[]) =>
  postJSONResult<SyntaxApplyResult>('/api/review/plans/syntax-apply', { fixes });

// ── S3 Projects ───────────────────────────────────────────

export interface S3ProjectCard {
  project_id: string;
  name: string;
  description: string;
  status: 'completed' | 'in_progress' | 'new';
  s3_uri: string;
  s3_folder: string;
  file_count: number;
  has_agent_output: boolean;
  project_type: string | null;
  metadata_loaded: boolean;
  created_at: string | null;
  annotator_count: number;
  new_files: string[];
  // Owning construction organization (from Monco), or null if unknown.
  organization?: string | null;
  // Set by the monco-sourced list: true while a pipeline is running for
  // this project (so the UI offers "Open" to watch it, not "Run" again).
  running?: boolean;
  // From Monco (by the folder's numeric id): when the project landed, its
  // uploads and Monco's own status. Available before S3 metadata loads.
  monco_project_id?: number | null;
  landed_at?: string | null;
  last_upload_at?: string | null;
  upload_count?: number | null;
  monco_status?: string | null;
  // Page-assignment progress (null when nothing is assigned yet).
  assignments?: AssignmentCounts | null;
  annotators?: AnnotatorProgress[];
  // Newest source file in S3, and the newest of that / last Monco upload /
  // landing — the list is sorted on last_file_at.
  latest_file_at?: string | null;
  last_file_at?: string | null;
  // Pages assigned but not all done or skipped.
  annotation_open?: boolean;
}

export interface AssignmentCounts {
  assigned: number;
  in_progress: number;
  completed: number;
  skipped: number;
  total: number;
  last_completed_at?: string | null;
}

export interface AnnotatorProgress extends AssignmentCounts {
  user_id: number;
  username: string;
  started_at: string | null;
}

export const getS3Projects = () =>
  fetchJSON<{ projects: S3ProjectCard[]; loading: boolean }>('/api/projects/s3');
export const refreshS3Projects = () =>
  postJSON('/api/projects/s3/refresh');
export const loadS3Project = (s3Uri: string) =>
  postJSON<{ project_id: string; status: string }>('/api/projects/s3/load', { s3_uri: s3Uri });
export const runPipeline = (s3Uri: string, flags?: string[]) =>
  postJSON<{ project_id: string; status: string }>('/api/survey/run', { s3_uri: s3Uri, flags });
export const forkWithNewFiles = (sourceS3Uri: string, flags?: string[]) =>
  postJSON<{ project_id: string; s3_uri: string; name: string; status: string; copied_files: number }>(
    '/api/survey/fork', { source_s3_uri: sourceS3Uri, flags }
  );

// Incremental "analyze new files": update the SAME project in place — analyze
// only the newly-added files and merge, never wiping prior annotations/extractions.
export const analyzeNewFiles = (s3Uri: string) =>
  postJSON<{ project_id: string; status: string }>(
    '/api/survey/analyze-new-files', { s3_uri: s3Uri }
  );
// New (unprocessed) source files for the currently-open project (detail view).
export const getProjectNewFiles = () =>
  fetchJSON<{ new_files: string[] }>('/api/project/new-files');

export interface IncrementalStatus {
  new_files?: string[];
  added?: { floor_plans?: number; diagrams?: number; boq_positions?: number };
  pending_actions?: string[];
  ran?: string[];
  updated_at?: string;
}
export const getIncrementalStatus = () =>
  fetchJSON<IncrementalStatus>('/api/project/incremental-status');
export const clearIncrementalPending = () =>
  postJSON('/api/project/incremental-status/clear');
export const getPipelineStatus = (projectId: string) =>
  fetchJSON<{ status: string; step: string; running: boolean; error?: string }>(
    `/api/survey/pipeline-status?project_id=${encodeURIComponent(projectId)}`
  );

// ── System ─────────────────────────────────────────────────

export const clearState = () => postJSON('/api/clear-state');
export const startupReady = () => postJSON('/api/startup-ready');

/** Per-artifact failure record returned by /api/publish.
 *  `kind` is a coarse category for grouping in the UI ("primary",
 *  "manifest", "lang:de", "zone_copy", …); `key` is the actual S3 key /
 *  filename so a retry can target a single artifact if we ever want
 *  that. `error` is the Python "ExceptionType: message" string. */
export interface PublishFailure {
  key: string;
  kind: string;
  error: string;
}

export interface PublishResult {
  /** "published" when every artifact uploaded; "partial" when one or
   *  more failed. The backend now wraps each upload in try/except so
   *  a single failure (Gemini timeout, S3 throttle, …) no longer
   *  aborts the whole publish — the rest still goes up. */
  status: 'published' | 'partial';
  location: string;
  /** Artifacts that landed, out of the set publish attempted. Page images
   *  are no longer rendered or published (#162), so the old files_copied /
   *  images_rendered counters are gone. */
  artifacts_written: number;
  artifacts_total: number;
  has_file_mapping: boolean;
  has_manifest: boolean;
  failures?: PublishFailure[];
}

export const publish = () => postJSON<PublishResult>('/api/publish');

// ── Per-plan publish ──────────────────────────────────────

export const publishPlan = (file: string, type: 'diagram' | 'floor_plan', page?: number) =>
  postJSON<{ status: string; key: string; location: string; artifacts: number }>(
    '/api/publish-plan', { file, type, page },
  );

export const publishDiagrams = () =>
  postJSON<{ status: string; location: string; artifacts: number; boq_items: number; zones: string[] }>(
    '/api/publish-diagrams',
  );

export const getPublishedPlans = () =>
  fetchJSON<Record<string, { type: string; file: string; page?: number; folder: string; location: string; published_at: string }>>(
    '/api/published-plans',
  );

// ── Collection ────────────────────────────────────────────

export const collectFile = (file: string, source: 'diagrams' | 'plans', page?: number) =>
  postJSON('/api/review/collect', { file, source, page });
export const uncollectFile = (file: string, source: 'diagrams' | 'plans', page?: number) =>
  deleteJSON('/api/review/collect', { file, source, page });
export const getCollectedState = () =>
  fetchJSON<{ diagrams: Record<string, {collectedAt: string}>, plans: Record<string, {collectedAt: string}> }>('/api/review/collected');

// ── Phase re-run ─────────────────────────────────────────

export const rerunPhase = (phase: 'research' | 'extraction') =>
  postJSON('/api/rerun-phase', { phase });

// ── Staged review ────────────────────────────────────────

export const groupDiagrams = () => postJSON('/api/review/diagrams/group');
export const matchDiagrams = () => postJSON('/api/review/diagrams/match');
// Import all reviewed diagrams into Monco as one distribution-board file
// (renders pages fresh, uploads, create-and-imports, replaces the prior one).
// Result variant so the server's error is surfaced (e.g. the 409 when an
// automatic post-analysis upload is already in progress for this project).
export const uploadDiagramsToDb = () =>
  postJSONResult<{ distribution_board_file_id: number; diagrams: number; pages: number; pdfs: number; deleted_previous_id: number | null }>(
    '/api/review/diagrams/upload-to-db');
export const matchPlan = (file: string, page: number) =>
  postJSON('/api/review/plans/match', { file, page });
// Result variants keep the HTTP status so a 409 (plan currently held —
// auto-processing or another user) is reported honestly, not as success.
export const matchPlanResult = (file: string, page: number) =>
  postJSONResult<{ boq_items: unknown[]; match_summary: MatchSummary }>(
    '/api/review/plans/match', { file, page });
export const updatePlanMatchItemResult = (file: string, page: number, itemIndex: number, lvMatch: LvMatch | null) =>
  patchJSONResult<{ boq_items: unknown[]; match_summary: MatchSummary }>('/api/review/plans/match-item', {
    file, page, item_index: itemIndex, lv_match: lvMatch,
  });
export const matchAllPlans = () =>
  postJSON<{ status: string; total: number }>('/api/review/plans/match-all');
export type PlanMatchState = 'pending' | 'running' | 'done' | 'failed' | 'skipped';
export interface MatchAllStatus {
  status: string;
  total?: number;
  done?: number;
  errors?: string[];
  plans?: Record<string, PlanMatchState>;
}
export const matchAllPlansStatus = () =>
  fetchJSON<MatchAllStatus>('/api/review/plans/match-all/status');

// ── Auto-processing after annotation completion (#166) ────────────
// The agent runs syntax check → LV match → publish on its own the moment an
// annotator finishes a plan. While that is in flight the same plan must not
// be matched or syntax-checked by hand, and the project-wide "all" actions
// are held back so two writers can't race each other.
export type AutoProcessStep = 'queued' | 'sync' | 'syntax_check' | 'lv_match' | 'publish';
export interface AutoProcessPlan {
  file: string;
  page: number;
  status: 'running' | 'done' | 'failed' | 'superseded';
  step: AutoProcessStep;
  error: string;
  published: boolean;
}
export interface AutoProcessStatus {
  busy: boolean;
  plans: AutoProcessPlan[];
}
export const annotationProcessingStatus = () =>
  fetchJSON<AutoProcessStatus>('/api/annotation-processing/status');

export const getDiagramReviewData = () => fetchJSON('/api/review/diagrams/data');
export const getPlanReviewData = (file?: string, page?: number) =>
  file != null && page != null
    ? fetchJSON(`/api/review/plans/data?file=${encodeURIComponent(file)}&page=${page}`)
    : fetchJSON('/api/review/plans/data');
export type PlanPublishStatus = 'published' | 'unpublished';

export const getPlanPublishStatus = () =>
  fetchJSON<Record<string, {
    published_at?: string;
    location?: string;
    floor_plan_id?: number;
    uploaded_at?: string;
    publish_status?: PlanPublishStatus;
  }>>('/api/review/plans/publish-status');

// Toggle a plan's Monco publish status. Only valid after upload-to-DB
// (the backend resolves floor_plan_id from published_plans.json). The
// Monco endpoint is super-admin only, so a non-admin account 403s.
export const setPlanPublishStatus = (file: string, page: number, publishStatus: PlanPublishStatus) =>
  patchJSON<{ floor_plan_id: number; publish_status: PlanPublishStatus }>(
    '/api/review/plans/set-publish-status', { file, page, publish_status: publishStatus });
// Result-variant with the server's error detail — "Publish all" uses it to
// distinguish stale ids (409, needs re-upload) from other failures.
export const setPlanPublishStatusResult = (file: string, page: number, publishStatus: PlanPublishStatus) =>
  patchJSONResult<{ floor_plan_id: number; publish_status: PlanPublishStatus }>(
    '/api/review/plans/set-publish-status', { file, page, publish_status: publishStatus });

export interface PlanAssignment {
  id: number;
  file: string;
  page: number;
  doc_type: string;
  zone_id: string;
  status: 'assigned' | 'in_progress' | 'completed' | 'skipped';
  username: string;
  assigned_at: string | null;
  completed_at: string | null;
  collected: boolean;
  has_annotation: boolean;
  has_match: boolean;
}

export interface PlanAssignmentsResponse {
  assignments: PlanAssignment[];
  sync_result: { synced: number; skipped: number; errors: string[] };
  auto_collected: number;
}

export const getPlanAssignments = () =>
  fetchJSON<PlanAssignmentsResponse>('/api/review/plans/assignments');

// ── Monco project / upload status (annotation tab) ────────
//
// Status enum shared by the Monco project + upload entities. Changing
// these requires a SUPER_ADMIN Monco account, so the backend (and Monco)
// will 403 non-admins — the result variant surfaces that message.

// Literal Monco StatusEnum values (lowercase, as stored/returned by the
// API). Used directly as <option> values so they match what GET returns.
export type MoncoStatus = 'created' | 'in_process' | 'in_validation' | 'error' | 'done';

export const MONCO_STATUS_VALUES: MoncoStatus[] = [
  'created', 'in_process', 'in_validation', 'error', 'done',
];

export const MONCO_STATUS_LABELS: Record<MoncoStatus, string> = {
  created: 'Created',
  in_process: 'In process',
  in_validation: 'In validation',
  error: 'Error',
  done: 'Done',
};

export interface MoncoUpload {
  id: number;
  status: MoncoStatus | null;
  name: string | null;
  file_names: string[];
}

export interface MoncoUploadsResponse {
  monco_project_id: number;
  project_status: MoncoStatus | null;
  web_base_url: string;
  uploads: MoncoUpload[];
}

// List the project's Monco uploads + each upload's file names, so the
// annotation tab can bucket assignments by upload.
export const getMoncoUploads = () =>
  fetchJSONResult<MoncoUploadsResponse>('/api/review/uploads');

export const setUploadStatus = (uploadId: number, status: MoncoStatus) =>
  patchJSONResult<{ upload_id: number; status: MoncoStatus }>(
    `/api/review/uploads/${uploadId}/status`, { status });

export const setProjectStatus = (status: MoncoStatus) =>
  patchJSONResult<{ monco_project_id: number; status: MoncoStatus }>(
    '/api/review/project/status', { status });

// ── Copy this project into another organization (BETA) ────

export interface OrgCopyPlan {
  file: string;
  page: number;
}

export interface OrgCopyStartResponse {
  project_id: number;
  name: string;
  organization_id: number;
  objects_copied: number;
  plans: OrgCopyPlan[];
}

export interface OrgCopyFinishResponse {
  project_id: number;
  plans_published: number;
  project_status: MoncoStatus | null;
  project_status_error?: string | null;
  diagrams: { diagrams: number; pages: number; pdfs: number } | null;
  diagrams_error?: string | null;
}

export const orgCopyStart = (organizationId: number, projectName?: string) =>
  postJSONResult<OrgCopyStartResponse>('/api/review/org-copy/start', {
    organization_id: organizationId,
    project_name: projectName || undefined,
  });

export const orgCopyFinish = (targetProjectId: number) =>
  postJSONResult<OrgCopyFinishResponse>('/api/review/org-copy/finish', {
    target_project_id: targetProjectId,
  });

// ── Cables (project-level cable estimation) ───────────────
export const getCables = () => getResults<CablesResult>('cables');
export const estimateCables = (instructions?: string) =>
  postJSON<{ status: string }>('/api/cables/estimate', instructions ? { instructions } : {});
export const getCablesStatus = () =>
  fetchJSON<{
    status: string; processed?: number; total?: number; total_m?: number; error?: string;
    last_instructions?: string;
  }>('/api/cables/status');

export interface CablesPublishStatus {
  enabled: boolean;
  published_at?: string | null;
  published_by?: string | null;
  uploaded_at?: string | null;
  boq_items?: number | null;
  total_m?: number | null;
}
export const getCablesPublishStatus = () =>
  fetchJSON<CablesPublishStatus>('/api/cables/publish-status');
export const setCablesPublishStatus = (enabled: boolean) =>
  postJSON<{ status: string; enabled: boolean }>('/api/cables/publish-status', { enabled });
export const uploadCablesToApp = () =>
  postJSON<{ status: string; boq_items: number; total_m: number; plans: number; s3: string }>(
    '/api/cables/upload', {});
